import { Injectable } from '@angular/core';
import type { WebinarScheduleResponse } from '@core-mkt/shared/components/webinar-schedule/webinar-schedule';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WebinarScheduleService {
  webinarScheduleSubject: Subject<WebinarScheduleResponse[]> = new Subject<WebinarScheduleResponse[]>();
  private readonly webinarContentSubject: Subject<WebinarScheduleResponse[]> = new Subject<WebinarScheduleResponse[]>();
  private readonly showScheduleSubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Updates the state of whether the webinar schedule should be displayed
   * and sets the webinar data to be used.
   *
   * @param showSchedule - A boolean indicating whether to show the webinar schedule.
   * @param webinarData - An array of webinar schedule data to be updated.
   */
  public setShowWebinarSchedule(showSchedule: boolean, webinarData: WebinarScheduleResponse[]): void {
    this.showScheduleSubject.next(showSchedule);
    this.webinarContentSubject.next(webinarData);
  }

  /**
   * Gets an observable stream of webinar schedule responses.
   *
   * @returns {Observable<WebinarScheduleResponse[]>} An observable emitting an array of webinar schedule responses.
   */
  get webinarSchedule(): Observable<WebinarScheduleResponse[]> {
    return this.webinarScheduleSubject.asObservable();
  }

  /**
   * Observable that indicates whether the webinar schedule should be displayed.
   *
   * @returns An `Observable<boolean>` that emits `true` if the webinar schedule should be shown, or `false` otherwise.
   */
  get showWebinarSchedule(): Observable<boolean> {
    return this.showScheduleSubject.asObservable();
  }

  /**
   * Gets an observable stream of webinar schedule content.
   *
   * @returns An observable that emits an array of `WebinarScheduleResponse` objects.
   */
  get webinarContent(): Observable<WebinarScheduleResponse[]> {
    return this.webinarContentSubject.asObservable();
  }
}
